import { Box, Paper } from '@mui/material';
import type { BlueprintSection, Language } from '../../../types';
import { useDefinitionContext } from '../../contexts/DefinitionContext';
import { useSegmentationEditor } from '../../hooks/useSegmentationEditor';
import { SegmentationColumnField } from './SegmentationColumnField';
import { SegmentationEditorHeader } from './SegmentationEditorHeader';
import { SegmentationHideColumnOption } from './SegmentationHideColumnOption';
import { SegmentationToggleRow } from './SegmentationToggleRow';
import {
  SEGMENTATION_EDITOR_TOUR_ELEMENT_ID,
  useSegmentationEditorTour,
} from './useSegmentationEditorTour';

interface SegmentationEditorProps {
  blueprintSection: BlueprintSection;
  sectionId: string;
}

/**
 * Editor for report segmentation: split report into sections (e.g. one XLS sheet per column value).
 */
export function SegmentationEditor({ blueprintSection, sectionId }: SegmentationEditorProps) {
  const { languages } = useDefinitionContext();
  const { closeTour } = useSegmentationEditorTour();
  const {
    sectionIndex,
    definitionSection,
    segmentation,
    handleSetSegmentation,
    handleClearSegmentation,
  } = useSegmentationEditor({ sectionId });

  const primaryLanguage = (languages[0] || 'en') as Language;
  const selectedColumnIds = new Set(definitionSection?.columns?.map((col) => col.id) || []);
  const availableColumns = blueprintSection.columns.filter(
    (col) => selectedColumnIds.has(col.id) && col.allowsSegmentation === true,
  );

  if (availableColumns.length === 0) {
    return null;
  }

  const isSegmentationEnabled = !!segmentation;

  const handleToggle = (checked: boolean) => {
    closeTour();

    if (checked && availableColumns.length > 0) {
      handleSetSegmentation(availableColumns[0].id);
    } else {
      handleClearSegmentation();
    }
  };

  return (
    <Paper id={SEGMENTATION_EDITOR_TOUR_ELEMENT_ID} sx={{ padding: 2 }}>
      <SegmentationEditorHeader />
      <Box display="flex" flexDirection="column" gap={2} maxWidth={'sm'}>
        <SegmentationToggleRow
          enabled={isSegmentationEnabled}
          onToggle={handleToggle}
          hasColumns={availableColumns.length > 0}
        />
        {isSegmentationEnabled && sectionIndex !== -1 && (
          <Box>
            <SegmentationColumnField
              name={`data.sections.${sectionIndex}.segmentation.columnId`}
              sectionIndex={sectionIndex}
              columns={availableColumns}
              primaryLanguage={primaryLanguage}
            />
            <SegmentationHideColumnOption
              name={`data.sections.${sectionIndex}.segmentation.hideColumn`}
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
}
